/**
 * Supplier profile › Audits (Audit Lite): the customer's own audits of this
 * supplier, the corrective actions they raised, and Start audit with the
 * supplier already chosen. Customer side and Helix only — never shown to the
 * supplier (see `canShowAuditsTab`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Audit, AuditSummary } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { AlertCircle, Eye, Loader2, Plus } from 'lucide-react';
import AuditActionsTable from '../audits/AuditActionsTable';
import AuditList from '../audits/AuditList';
import StartAuditDialog from '../audits/StartAuditDialog';
import { useAudits } from '../audits/useAudits';
import type { AuditSummaryState } from './useAuditSummary';

interface AuditsTabProps {
  supplierId: number;
  supplierName: string;
  /** Customer users start audits and work actions; Helix staff read only. */
  canEdit: boolean;
  /** Owned by the page, which also shows it as the tab badge. */
  summaryState: AuditSummaryState;
}

const CARD_LABELS = ['Last audit', 'Valid until', 'Open actions', 'Overdue'];

interface SummaryCard {
  label: string;
  value: string;
  hint?: string;
  className?: string;
}

/** "Valid until": the latest valid completion, else why there is none. */
function validUntil(summary: AuditSummary, audits: Audit[], formatDate: (d: string) => string): Pick<SummaryCard, 'value' | 'className'> {
  if (summary.valid_until) return { value: formatDate(summary.valid_until) };
  if (audits.some((a) => a.status === 'completed' && !a.valid_until)) return { value: 'No expiry' };
  if (audits.some((a) => a.status === 'expired')) return { value: 'Expired', className: 'text-red-600' };
  return { value: '—' };
}

/**
 * The four summary cards: Last audit · Valid until · Open actions · Overdue.
 *
 * @param summary - the supplier's audit summary
 * @param audits - its audits (to tell "no expiry" from "expired")
 * @param formatDate - tenant date formatter
 */
export function auditSummaryCards(summary: AuditSummary, audits: Audit[], formatDate: (d: string) => string): SummaryCard[] {
  const count = summary.audit_count;
  return [
    {
      label: 'Last audit',
      value: summary.last_audit_date ? formatDate(summary.last_audit_date) : 'Never',
      hint: `${count} audit${count === 1 ? '' : 's'}`,
    },
    { label: 'Valid until', ...validUntil(summary, audits, formatDate) },
    { label: 'Open actions', value: String(summary.open_actions), hint: 'Including overdue' },
    {
      label: 'Overdue',
      value: String(summary.overdue_actions),
      className: summary.overdue_actions > 0 ? 'text-red-600' : undefined,
    },
  ];
}

/**
 * Audits tab of the supplier profile.
 */
export default function AuditsTab({ supplierId, supplierName, canEdit, summaryState }: AuditsTabProps) {
  const { formatDate } = useFormatters();
  const { audits, loading, error, reload } = useAudits({ supplier: supplierId });
  const [startOpen, setStartOpen] = useState<boolean>(false);
  // Stable: StartAuditDialog reloads whenever this object changes.
  const supplier = useMemo(() => ({ id: supplierId, name: supplierName }), [supplierId, supplierName]);

  // The page loaded the summary for the tab badge; refresh it on each visit so
  // actions closed since (here or under Questionnaires › Audits) are counted.
  const { reload: reloadSummary, loading: summaryLoadingNow } = summaryState;
  const visited = useRef<boolean>(false);
  useEffect(() => {
    if (visited.current) return;
    visited.current = true;
    if (!summaryLoadingNow) void reloadSummary();
  }, [reloadSummary, summaryLoadingNow]);

  const { summary, loading: summaryLoading, error: summaryError } = summaryState;
  const cards = summary && !summaryLoading ? auditSummaryCards(summary, audits, formatDate) : null;
  const startButton = canEdit ? (
    <Button onClick={() => setStartOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Start audit</Button>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Audits</h3>
          <p className="text-sm text-muted-foreground">
            Your own audits of {supplierName} and the actions they raised. The supplier doesn&apos;t see them.
          </p>
        </div>
        {startButton}
      </div>
      {!canEdit && (
        <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <Eye className="h-4 w-4" />Read-only: audits are started and completed by the customer&apos;s own team.
        </p>
      )}

      {summaryError ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <span className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {summaryError}
            </span>
            <Button variant="outline" size="sm" onClick={() => void summaryState.reload()}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards
            ? cards.map((card) => (
              <Card key={card.label}>
                <CardContent className="pt-4 pb-3">
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.className ?? ''}`}>{card.value}</p>
                  {card.hint && <p className="text-xs text-muted-foreground mt-0.5">{card.hint}</p>}
                </CardContent>
              </Card>
            ))
            : CARD_LABELS.map((label) => (
              <Card key={label}>
                <CardContent className="pt-4 pb-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <Loader2 className="h-5 w-5 my-1.5 animate-spin text-muted-foreground" aria-label={`Loading ${label}`} />
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      <AuditList audits={audits} loading={loading} error={error} onRetry={() => void reload()} hideSupplier
        onChanged={() => { void reload(); void reloadSummary(); }}
        emptyText={`No audits for ${supplierName} yet.`} emptyAction={startButton ?? undefined} />

      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Corrective actions</h3>
        <AuditActionsTable scope={{ supplier: supplierId }} hideSupplier initialFilter="open" readOnly={!canEdit}
          onChanged={() => { void reloadSummary(); void reload(); }} />
      </div>

      {canEdit && <StartAuditDialog open={startOpen} onOpenChange={setStartOpen} supplier={supplier} />}
    </div>
  );
}
